
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { defineComponent, PropType, reactive, Ref, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ContentAdaptLayout from '../../../../_components/content-adapt-layout/content-adapt-layout.vue'

import {
  FlowControlSettings,
  FlowUnits,
  GroupPolicySettingsService,
  GroupPolicyTypes,
} from '../../group-policy-settings.service'
import { OperateType } from '@/views/dashboard/_components/group-policy-settings/group-policy-drawer.service'
import DetailsSubscribeBar from '@/views/dashboard/_components/subscribe-bar/details-subscribe-bar.vue'
import { PermissionFe } from '@/services/service-permission.service'

const MIN_SPEED = 100

export default defineComponent({
  components: {
    DetailsSubscribeBar,
    ContentAdaptLayout
  },
  props: {
    operateType: {
      type: String as PropType<'edit'|'add'|'read'>,
      default: 'edit'
    },
    policyId: {
      type: String,
      default: undefined
    },
    name: {
      type: String,
      default: ''
    },
    activeName: {
      type: String,
      default: ''
    },
    hasPermission: {
      type: Boolean,
      default: false
    }
  },
  setup (props, context) {
    const { t } = useI18n()
    const injector = useReflectiveInjector()
    const groupPolicySettingsService = injector.get(GroupPolicySettingsService)

    const loading = ref<boolean>(true)
    const uploadMinSpeed = ref<number>(MIN_SPEED)
    const downloadMinSpeed = ref<number>(MIN_SPEED)

    const DEFAULT_SPEED = 128

    const data = reactive({
      useAble: props.hasPermission,
      hasEdit: false
    })
    const DEFAULT_PARAMS = {
      switch: false,
      up_speed: DEFAULT_SPEED,
      down_speed: DEFAULT_SPEED,
      up_unit: FlowUnits.KB,
      down_unit: FlowUnits.KB
    }

    const strategy = reactive<FlowControlSettings>({
      ...DEFAULT_PARAMS
    })
    function getSettings () {
      groupPolicySettingsService.getSettings({
        id: props.policyId,
        type: GroupPolicyTypes.FlowControl
      }).then(response => {
        const settingsData = response.strategy
        Object.assign(strategy, {
          ...DEFAULT_PARAMS
        }, settingsData)
      }).finally(() => {
        loading.value = false
      })
    }

    function saveSettings () {
      if (!props.hasPermission) return
      groupPolicySettingsService.saveSettings({
        id: props.policyId,
        type: GroupPolicyTypes.FlowControl,
        strategy: JSON.stringify(strategy)
      }).then(() => {
        groupPolicySettingsService.updateEditTab(false)
        context.emit('save')
      })
    }
    function cancel () {
      context.emit('cancel')
    }
    groupPolicySettingsService.hasEditTab.subscribe((hasEdit: boolean) => {
      data.hasEdit = hasEdit
    })
    groupPolicySettingsService.saveTab.subscribe((name: string) => {
      if (name === 'flow-control') {
        saveSettings()
      }
    })
    watch(() => props.activeName, (newV) => {
      if (newV === 'flow-control') {
        getSettings()
      }
    })
    function reorganizeSpeed (refMinData: Ref, refData: FlowControlSettings, key: string, unit: number) {
      if (unit === FlowUnits.MB) {
        refMinData.value = 1
      } else {
        refMinData.value = MIN_SPEED
        if (refData[key] < MIN_SPEED) {
          refData[key] = MIN_SPEED
        }
      }
    }

    watch(() => [strategy.up_unit], ([unit]) => {
      reorganizeSpeed(uploadMinSpeed, strategy, 'up_speed', unit)
    })

    watch(() => [strategy.down_unit], ([unit]) => {
      reorganizeSpeed(downloadMinSpeed, strategy, 'down_speed', unit)
    })
    watch(() => props.hasPermission, (newV) => {
      if (newV) {
        data.useAble = true
        getSettings()
      } else {
        data.useAble = false
      }
    })
    getSettings()

    return {
      t,
      PermissionFe,
      OperateType,
      ...toRefs(data),
      loading,
      uploadMinSpeed,
      downloadMinSpeed,
      maxSpeed: 9999,
      FlowUnits,
      strategy,
      cancel,
      calcBtnDisableStatus () {
        if (data.useAble) {
          return !data.hasEdit && props.operateType !== OperateType.add  && 0
        }
        return !data.useAble
      },
      handlerSave () {
        saveSettings()
      },
      setChangeStatus () {
        groupPolicySettingsService.updateEditTab(true)
      }
    }
  }
})
